import React from 'react';

/**
 * Regex that matches URLs in plain text.
 * Handles http(s), www., and bare domains with common TLDs.
 */
const URL_REGEX =
  /(?:https?:\/\/|www\.)[^\s<>"'`,;!?\])]*[^\s<>"'`,;!?\].):]|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+(?:com|org|net|io|co|dev|app|me|info|biz|edu|gov|xyz|ai|tech|uk|de|fr|nl|be|at|ch|es|it|pt|pl|cz|se|no|dk|fi|ru|jp|cn|kr|in|au|nz|ca|us|br|mx|ar|za)(?:\/[^\s<>"'`,;!?\])]*[^\s<>"'`,;!?\].):"])?/gi;

/** Style for clickable links */
const linkStyle: React.CSSProperties = {
  color: '#5E84ED',
  textDecoration: 'underline',
  cursor: 'pointer',
  wordBreak: 'break-all',
};

/**
 * Ensure a URL has a protocol prefix so the browser opens it correctly.
 */
function ensureProtocol(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

/**
 * Takes a plain-text string and returns React elements where URLs are
 * wrapped in clickable <a> tags. Non-URL text is left as-is.
 */
export function linkify(text: string): React.ReactNode {
  if (!text) return text;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset the regex state
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    const url = match[0];
    const start = match.index;

    // Push text before the match
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    parts.push(
      <a
        key={`link-${start}`}
        href={ensureProtocol(url)}
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    );

    lastIndex = start + url.length;
  }

  // Push remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // If no links were found, return the original string (avoids unnecessary wrapping)
  return parts.length === 0 ? text : parts;
}

/**
 * Escape HTML special characters to prevent XSS.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * HTML version of linkify for use with dangerouslySetInnerHTML or contentEditable.
 * Escapes HTML first, then wraps URLs in <a> tags. Newlines become <br/>.
 */
export function linkifyHtml(text: string): string {
  if (!text) return text;

  // Escape HTML first to prevent XSS
  const escaped = escapeHtml(text);

  // Reset the regex state
  URL_REGEX.lastIndex = 0;

  // Linkify URLs in the escaped string
  const linked = escaped.replace(URL_REGEX, (url) => {
    const href = ensureProtocol(url);
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#5E84ED;text-decoration:underline;cursor:pointer;word-break:break-all;">${url}</a>`;
  });

  // Convert newlines to <br/> for proper display
  return linked.replace(/\n/g, '<br/>');
}
